/**
 * WorkflowRetryStepButton - Botão "Reexecutar etapa" (BR-WF-EXEC-001).
 *
 * Usado somente na página Execuções. Chama `workflow_retry_parked_step`, que
 * reentra na etapa parada pelo motor oficial, sem repetir a ação já concluída.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { toast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';
import { mensagemReexecucao, type ResultadoReexecucao } from '@/lib/workflow/retryParkedStep';

const logger = createLogger('WorkflowRetryStepButton');

export function WorkflowRetryStepButton({ instanceId }: { instanceId: string }) {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  const reexecutar = useMutation({
    mutationFn: async () => {
      // .call preserva o receptor do cliente em RPC não tipada
      const { data, error } = await (supabase.rpc as any).call(supabase, 'workflow_retry_parked_step', {
        p_instance_id: instanceId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      return data as ResultadoReexecucao;
    },
    onSuccess: (r) => {
      toast({ title: r.avancou ? 'Etapa reexecutada' : 'Nada mudou', description: mensagemReexecucao(r) });
      qc.invalidateQueries({ queryKey: ['workflow-execution-detail'] });
      qc.invalidateQueries({ queryKey: ['workflow-execution-log'] });
    },
    onError: (e: Error) => {
      logger.error('Falha ao reexecutar etapa', { error: e.message });
      toast({ title: 'Não foi possível reexecutar', description: e.message, variant: 'destructive' });
    },
  });

  const aoClicar = async () => {
    const ok = await confirmDialog({
      title: 'Reexecutar etapa parada?',
      description:
        'O sistema vai conferir de novo as ligações desta etapa com os dados atuais e com a versão publicada do fluxo. A ação desta etapa não será repetida.',
      variant: 'warning',
      confirmText: 'Reexecutar',
    });
    if (ok) reexecutar.mutate();
  };

  return (
    <>
      <Button type="button" size="sm" variant="outline" className="gap-1" disabled={reexecutar.isPending} onClick={aoClicar}>
        <RotateCcw className="h-3.5 w-3.5" /> Reexecutar etapa
      </Button>
      {ConfirmDialogComponent}
    </>
  );
}
